"use client";

/**
 * The staff hop `useStaffTaskDeepLink` has finished applying, published so the
 * one consumer that must not fetch mid-hop (TasksPanel) can tell.
 *
 * ## Why a published key, and why it must be readable during render
 *
 * "A staff hop is pending" used to be spelled "`?as=` is on the URL", and the
 * hop's own `stripHopParams()` was what released it. ENG-5147 keeps the params
 * so the URL stays reloadable, which removed that release — so the gate became
 * `staffHopPending`, and two codex rounds on #2649 found the same hole from two
 * directions:
 *
 *  1. Comparing the impersonation TARGET is blind to a hop that does not change
 *     it: staff already impersonating A in workspace X, opening
 *     `/task/<id>?as=A&w=Y`. The target matches from the first render, so the
 *     gate opened while the workspace switch was still in flight, `getTask`
 *     went out under X and 404'd on a perfectly good Y task.
 *  2. A flag the hook sets from its own `useEffect` is too late. React runs
 *     passive effects child-first, so TasksPanel's route-loading effect runs
 *     BEFORE AppShell's hook effect in the same commit — it had already read
 *     `pending === false` during render and issued the request. Worse, that
 *     request holds `loadingRef`, so the re-run after the hop can find the lock
 *     held and do nothing, leaving the 404 on screen until a manual reload.
 *
 * So the signal is a value the gate can read **during render**: the key of the
 * hop the hook has applied, in the same `as|w` form the URL names. A URL whose
 * key differs from the settled one is pending, no matter which effect runs
 * first. The subscription exists only to re-render when the key changes; the
 * decision itself never depends on effect ordering.
 *
 * ## Why "settled", not "succeeded"
 *
 * The key is published on every ending — success, a refused mint, and the
 * not-a-member fallback where `beginImpersonation` lands in the target's home
 * org instead of the workspace asked for. A requested workspace that never
 * arrives would otherwise read as pending forever, trading the 404 above for
 * an infinite skeleton, which is the worse failure. Settled means "this hook
 * is done with that URL", not "the URL got what it wanted".
 */

/** `as|w` of the last hop the hook finished applying. Null before the first. */
let settled: string | null = null;
const listeners = new Set<() => void>();

/** Called by the hook on every terminal path, including the ones that failed. */
export function markStaffHopSettled(hopKey: string): void {
  if (settled === hopKey) return;
  settled = hopKey;
  for (const listener of listeners) listener();
}

export function subscribeStaffHop(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function staffHopSettled(): string | null {
  return settled;
}

/** Server snapshot for `useSyncExternalStore`: no hop can have settled in a
 * render that has not happened in a browser yet, so SSR and hydration agree. */
export function staffHopSettledOnServer(): null {
  return null;
}

/** Test-only reset; the store is module state that would otherwise leak between
 * cases in a single Node process. */
export function resetStaffHopState(): void {
  settled = null;
  for (const listener of listeners) listener();
}
